import axios from "axios";
import classnames from "classnames";
import camelCase from "lodash-es/camelCase";
import get from "lodash-es/get";
import upperFirst from "lodash-es/upperFirst";
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { TouchScrollable } from "react-scrolllock"; // TODO: WP-1418 - replace by body-scroll-lock package
import { useRenderOn } from "src/hooks";
import styled from "styled-components";

import environment from "../../../../../../../environment";
import i18n from "../../../../../../../i18n";
import theme, { media } from "../../../../../../../theme";
import {
  IMenuContentNormed,
  MenuDevice,
} from "../../../../../../../types/global";
import Hyperlink from "../../../../../../Hyperlink";
import Icon from "../../../../../../Icon";
import {
  isLaptopOrUp,
  isTabletOrDown,
} from "../../../../../../RenderOn/RenderOn.component";

import * as Slices from "./MenuSlices";

const List = styled.ul`
  overflow-y: auto;
  flex-grow: 1;
  padding: 0 20px;
  margin: 0;
  list-style: none;

  ${media.laptop} {
    display: flex;
    justify-content: center;
    height: initial;
    overflow: initial;
    padding: 0;
    pointer-events: auto;
  }
`;

const WrapNested = styled.div`
  position: fixed;
  right: 0;
  left: 0;
  z-index: 20;
  padding: 25px 0 40px 0;
  background-color: white;
`;

const NestedList = styled.ul`
  display: flex;
  list-style: none;
  padding: 0 20px;
  margin-top: 0;
  overflow: initial;
`;

const ListItem = styled.li`
  display: flex;
  flex-flow: row nowrap;
  flex: 1;
  min-width: 0;

  padding: 0;
  list-style: none;
  margin: 0 20px;
`;

interface ILiWrapper {
  styleWrapper?: {
    backgroundColor?: string | null;
    color?: string | null;
  };
}
export const LiWrapper = styled.li<ILiWrapper>`
  display: flex;
  border-bottom: 1px solid ${theme.colors.lightGrey};
  align-items: center;
  height: 42px;
  padding-left: 8px;
  font-family: ${theme.fonts.regular};

  & i {
    margin: 0 6px 0 -6px !important;
  }

  &.subTitle,
  &.subTitle a {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  &.bold {
    font-family: ${theme.fonts.subtitle};
  }

  ${media.laptop} {
    position: relative;
    flex-grow: 1;
    width: auto;
    height: 51px;
    padding: 0;
    margin: 0 auto;
    border: initial;

    background-color: ${({ styleWrapper }: ILiWrapper): string =>
      (styleWrapper && styleWrapper.backgroundColor) || "initial"};
    color: ${({ styleWrapper }: ILiWrapper): string =>
      (styleWrapper && styleWrapper.color) || "#252024"};
    font-size: 11px;
    font-weight: normal;
    letter-spacing: 0.9px;
    line-height: 13px;
    text-align: center;
    text-transform: uppercase;

    & a {
      padding: 13px 26px 11px 26px;
    }

    &.hover {
      color: ${({ styleWrapper }: ILiWrapper): string =>
        (styleWrapper && styleWrapper.color) || "#856e57"};
      box-shadow: 0px -2px 0px 0px #a9a395;
      z-index: 1;
    }

    &.subTitle,
    &.subTitle a {
      justify-content: center;
    }
  }
`;

const StyledHyperlink = styled(Hyperlink)`
  height: 100%;
  display: flex;
  align-items: center;
  flex-grow: 1;

  ${media.laptop} {
    height: 100%;
  }
`;

const Mask = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
`;

const pascalCase = (text: string): string => upperFirst(camelCase(text));

export const isShowOnThisDevice = (device: MenuDevice): boolean =>
  device === "tous" ||
  (device === "mobile" && isTabletOrDown()) ||
  (device === "desktop" && isLaptopOrUp());

const convertBrStringToBrJSX = (text?: string | null): JSX.Element[] | null =>
  text
    ? text
        .split(/<br[ /]*>/)
        .map((el: string, index: number, arr: string[]) => (
          <Fragment key={index}>
            {el}
            {index < arr.length - 1 && <br />}
          </Fragment>
        ))
    : null;

/**
 * Link in a li wrapper
 * @param param0
 */
export const LiLink = ({
  href,
  text,
  color,
  backgroundColor,
  hasChildren,
  onClick,
  onMouseEnter,
  onMouseLeave,
  classname,
  key,
}: {
  href?: string;
  text?: string;
  color?: string;
  backgroundColor?: string;
  hasChildren?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  classname?: string[];
  key?: string;
}): JSX.Element => (
  <LiWrapper
    key={key}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    className={
      classnames({
        bold: hasChildren,
        subTitle: true,
      }) + (classname ? " " + classname.join(" ") : "")
    }
    styleWrapper={{ color, backgroundColor }}
  >
    <MenuLink hasChildren={hasChildren} href={href} text={text} color={color} />
  </LiWrapper>
);

interface ITextColor {
  textColor?: string | null;
}
const Text = styled.span<ITextColor>`
  color: ${({ textColor }: ITextColor): string => textColor ?? "inherit"};
  white-space: nowrap;
`;
interface IMenuLink {
  hasChildren?: boolean;
  href?: string | null;
  text?: string | null;
  color?: string | null;
}
/**
 * Link in menu
 * @param param0
 */
const MenuLink = ({
  hasChildren,
  href,
  text,
  color,
}: IMenuLink): JSX.Element | null =>
  isTabletOrDown() && hasChildren ? (
    <>
      <Text textColor={color}>
        {(text || "").replace(/\s*<br[ /]*>\s*/, " ")}
      </Text>
      {<Icon name="chevron-droit" size={24} color={color ?? undefined} />}
    </>
  ) : (
    <StyledHyperlink to={href}>
      <Text textColor={color}>
        {isTabletOrDown()
          ? (text || "").replace(/\s*<br[ /]*>\s*/, " ")
          : convertBrStringToBrJSX(text)}
      </Text>
    </StyledHyperlink>
  );

/**
 * Show back to previous menu button
 * @param menu
 * @param path
 * @param setPath
 */
const backToPreviousMobileMenu = (
  menu: IMenuContentNormed[],
  path: number[],
  setPath: Dispatch<SetStateAction<number[]>>
): JSX.Element | null =>
  path.length ? (
    <LiWrapper
      className={"bold"}
      onClick={(): void => setPath((pth: number[]) => pth.slice(0, -1))}
    >
      <Icon name="chevron-gauche" size={24} />
      {(
        (get(menu, `${path[0]}.text`) || "") +
        (path.length > 1
          ? " / " + get(menu, `${path[0]}.children.${path[1]}.text`)
          : "")
      ).replace(/\s*<br[ /]*>\s*/, " ")}
    </LiWrapper>
  ) : null;

/**
 * Render menu on mobile
 * @param menu
 * @param path
 * @param setPath
 */
const renderMobileMenu = (
  menu: IMenuContentNormed[],
  path: number[],
  setPath: Dispatch<SetStateAction<number[]>>,
  closeMenu: () => void
): Array<JSX.Element | JSX.Element[] | null> =>
  (
    (path.length ? get(menu, path.join(".children.") + ".children") : menu) ||
    []
  ).map((content: IMenuContentNormed, index: number):
    | JSX.Element
    | JSX.Element[]
    | null => {
    const { type, text, href, device, children, color } = content;
    const hasChildren = !!(children && children.length > 0);

    if (
      (path.length === 0 && device && isShowOnThisDevice(device)) ||
      (path.length === 1 &&
        type &&
        ["links", "image_link", "html_link", "grid_links"].includes(type)) ||
      path.length > 1
    ) {
      return LiLink({
        hasChildren,
        href,
        key: `nav-item-${type || "main"}-${index}`,
        onClick: (): void | false =>
          hasChildren
            ? setPath((pth: number[]) =>
                path.length === 0
                  ? [index, ...(pth || [])]
                  : [...(pth || []), index]
              )
            : closeMenu(),
        text,
        color,
      });
    }

    // type icon_links
    if (type === "icon_links" && children && children.length) {
      return children.map((child: IMenuContentNormed, i: number) =>
        LiLink({
          href: child.href,
          key: `nav-item-icon_links-${index}-${i}`,
          text: child.text,
          color,
        })
      );
    }

    return null;
  });

/**
 * Render main desktop menu
 * @param menu
 * @param path
 * @param setPath
 */
const renderMainDesktopMenu = (
  menu: IMenuContentNormed[],
  path: number[],
  setPath: Dispatch<SetStateAction<number[]>>
): Array<JSX.Element | null> => {
  return menu.map((content: IMenuContentNormed, index: number) => {
    const { text, href, device, color, backgroundColor } = content;

    return device && isShowOnThisDevice(device)
      ? LiLink({
          backgroundColor: backgroundColor
            ? backgroundColor
            : index === path[0]
            ? "white"
            : "initial",
          classname: [index === path[0] ? "hover" : ""],
          color,
          hasChildren: !!(content.children && content.children.length),
          href,
          key: `nav-item-${index}`,
          onMouseEnter: (): void =>
            setPath((pth: number[]) => [index, ...pth.slice(1)]),
          text,
        })
      : null;
  });
};

/**
 * Render menu on laptop or up
 * @param menu
 * @param path
 * @param setPath
 */
const renderNestedDesktopMenu = (
  menu: IMenuContentNormed[],
  path: number[],
  setPath: Dispatch<SetStateAction<number[]>>
): JSX.Element | null => {
  const children = get(menu, `${path[0]}.children`) || [];

  return (
    <NestedList className="container">
      {children.map((slice: IMenuContentNormed, index: number) => {
        const { type } = slice;
        const name = (type && pascalCase(type)) || "error";
        const Slice = Slices[name] || Slices.error;

        return (
          <ListItem key={`${type}-${index}`}>
            <Slice tab={slice} menu={menu} path={path} setPath={setPath} />
          </ListItem>
        );
      })}
    </NestedList>
  );
};

export interface IProps {
  closeMenu: () => void;
}

interface ILangMenu {
  [key: string]: IMenuContentNormed[];
}

interface IAxiosData {
  data: ILangMenu;
}

const getMenuFromNode = async (): Promise<ILangMenu> =>
  axios
    .get(`${environment.REACT_APP_API_ENDPOINT}/menu?lang=${i18n.language}`)
    .then(({ data }: IAxiosData): ILangMenu => data);

/**
 * Content of the menu
 * @param props
 */
export const AppContentMenuList = (props: IProps): JSX.Element | null => {
  const [menu, setMenu] = useState<IMenuContentNormed[]>([]);
  const [path, setPath] = useState<number[]>([]);

  const { mdOrDown } = useRenderOn();

  /**
   * Get prismic data from global var or from node
   */
  useEffect((): (() => void) => {
    let mounted = true;

    const getMenu = async (): Promise<IMenuContentNormed[]> => {
      if (!window.__initialMenuContent?.[i18n.language]) {
        window.__initialMenuContent = {
          ...window.__initialMenuContent,
          ...(await getMenuFromNode()),
        };
      }

      return window.__initialMenuContent?.[i18n.language];
    };

    getMenu().then(
      (data: IMenuContentNormed[]): void | false => mounted && setMenu(data)
    );

    return (): void => {
      mounted = false;
    };
  }, []);

  const listRef = useRef<HTMLUListElement>(null);
  const show =
    path.length > 0 && (get(menu, `${path[0]}.children`) || []).length > 0;
  const nestedMenu = createPortal(
    <WrapNested
      id="modal-menu"
      style={{
        ...(show ? {} : { display: "none" }),
        top: listRef.current?.getBoundingClientRect().bottom || 0,
      }}
    >
      {renderNestedDesktopMenu(menu, path, setPath)}
    </WrapNested>,
    document.body
  );

  return (
    <TouchScrollable>
      {mdOrDown ? (
        <List>
          {backToPreviousMobileMenu(menu, path, setPath)}
          {renderMobileMenu(menu, path, setPath, props.closeMenu)}
        </List>
      ) : (
        <>
          {path.length > 0 && <Mask />}
          <div onMouseLeave={(): void => setPath([])}>
            <List ref={listRef}>
              {renderMainDesktopMenu(menu, path, setPath)}
            </List>
            {nestedMenu}
          </div>
        </>
      )}
    </TouchScrollable>
  );
};

export default AppContentMenuList;
